/**
 * Addon set reconcile.
 * After cart updates (and on cart page load) it brings addons back in line with
 * their parent and folds duplicate sets into one. Requires addon-set-relationships.js.
 */
(function () {
  'use strict';

  var SOURCE = 'addon-set-reconcile';
  var running = false;

  // Targets the cart refused (`lineKey@quantity`), so a stock-capped line is not retried on every update
  var unreachable = {};

  function targetKey(lineKey, quantity) {
    return lineKey + '@' + quantity;
  }

  // Duplicate sets whose fold failed, by parent line key, so the fold is not retried
  var unfoldable = {};

  // Duplicate compositions fold into the first set seen; each fold is returned so it only completes once its raise lands
  function buildTargets(sets) {
    var targets = {};
    var groups = [];
    var groupByComposition = {};

    sets.forEach(function (set) {
      var group = groupByComposition[set.composition];

      if (!group || unfoldable[set.parent.key]) {
        group = { primary: set, duplicates: [] };
        groups.push(group);
        if (!groupByComposition[set.composition]) groupByComposition[set.composition] = group;
        targets[set.parent.key] = set.parent.quantity;
        return;
      }

      group.duplicates.push(set);
      targets[group.primary.parent.key] += set.parent.quantity;
      targets[set.parent.key] = 0;

      set.children.forEach(function (child) {
        targets[child.key] = 0;
      });
    });

    var folds = [];

    groups.forEach(function (group) {
      var primary = group.primary;
      var parentQuantity = targets[primary.parent.key];

      primary.children.forEach(function (child) {
        targets[child.key] = parentQuantity * window.AddonSetRelationships.getQtyRatio(child);
      });

      if (group.duplicates.length === 0) return;

      var duplicateKeys = [];
      group.duplicates.forEach(function (set) {
        duplicateKeys.push(set.parent.key);
        set.children.forEach(function (child) {
          duplicateKeys.push(child.key);
        });
      });

      folds.push({
        primaryKeys: [primary.parent.key].concat(
          primary.children.map(function (child) {
            return child.key;
          })
        ),
        duplicateKeys: duplicateKeys,
        duplicateParentKeys: group.duplicates.map(function (set) {
          return set.parent.key;
        })
      });
    });

    return { targets: targets, folds: folds };
  }

  function buildCorrections(cart, targets) {
    var corrections = {};
    var changed = false;

    cart.items.forEach(function (item) {
      if (!Object.prototype.hasOwnProperty.call(targets, item.key)) return;
      if (targets[item.key] === item.quantity) return;
      if (unreachable[targetKey(item.key, targets[item.key])]) return;

      corrections[item.key] = targets[item.key];
      changed = true;
    });

    return changed ? corrections : null;
  }

  // The cart page renders the drawer too, so both surfaces are re-rendered after a repair
  function getCartSurfaces() {
    var surfaces = [];
    var page = document.querySelector('cart-items');
    var drawer = document.querySelector('cart-drawer-items');

    if (page) surfaces.push(page);
    if (drawer) surfaces.push(drawer);

    return surfaces;
  }

  function sectionsBody(surfaces) {
    var names = [];

    surfaces.forEach(function (surface) {
      surface.getSectionsToRender().forEach(function (section) {
        if (names.indexOf(section.section) === -1) names.push(section.section);
      });
    });

    return {
      sections: names,
      sections_url: window.location.pathname
    };
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });
  }

  function quantitiesByKey(cart) {
    var current = {};

    cart.items.forEach(function (item) {
      current[item.key] = item.quantity;
    });

    return current;
  }

  /**
   * Raises go one at a time through /cart/change.js, which checks stock; everything else
   * goes in one final /cart/update.js. A fold only empties the duplicate once the surviving
   * set has reached its target, otherwise both sets are left as they were.
   */
  function applyCorrections(cart, corrections, plan, surfaces) {
    var current = quantitiesByKey(cart);
    var raises = [];
    var drops = {};
    var duplicateKey = {};

    plan.folds.forEach(function (fold) {
      fold.duplicateKeys.forEach(function (key) {
        duplicateKey[key] = true;
      });
    });

    Object.keys(corrections).forEach(function (key) {
      if (corrections[key] > (current[key] || 0)) {
        raises.push(key);
      } else {
        drops[key] = corrections[key];
      }
    });

    if (raises.length === 0 && plan.folds.length === 0) return sendUpdates(drops, surfaces);

    var latest = cart;

    var raised = raises.reduce(function (chain, key) {
      return chain.then(function () {
        // A raise can re-key another line, so skip lines the cart no longer has
        if (!Object.prototype.hasOwnProperty.call(quantitiesByKey(latest), key)) return;

        return raiseLine(key, corrections[key]).then(function (parsed) {
          if (parsed) latest = parsed;
        });
      });
    }, Promise.resolve());

    return raised.then(function () {
      var settled = quantitiesByKey(latest);
      var has = function (key) {
        return Object.prototype.hasOwnProperty.call(settled, key);
      };
      var updates = {};

      // Clamped to what the cart holds, so this call never raises
      raises.forEach(function (key) {
        if (!has(key)) return;
        updates[key] = Math.min(corrections[key], settled[key]);
      });

      Object.keys(drops).forEach(function (key) {
        if (duplicateKey[key] || !has(key)) return;
        updates[key] = Math.min(drops[key], settled[key]);
      });

      plan.folds.forEach(function (fold) {
        var landed = fold.primaryKeys.every(function (key) {
          return has(key) && settled[key] >= plan.targets[key];
        });

        if (landed) {
          fold.duplicateKeys.forEach(function (key) {
            if (has(key)) updates[key] = 0;
          });
          return;
        }

        // Raise did not land: restore the surviving set and leave the duplicate untouched
        fold.primaryKeys.forEach(function (key) {
          if (has(key) && Object.prototype.hasOwnProperty.call(current, key)) {
            updates[key] = Math.min(settled[key], current[key]);
          }
        });

        fold.duplicateParentKeys.forEach(function (key) {
          unfoldable[key] = true;
        });

        console.warn('[addon-set] could not fold a duplicate set; left both sets as they were');
      });

      if (Object.keys(updates).length === 0) return latest;

      return sendUpdates(updates, surfaces);
    });
  }

  function sendUpdates(updates, surfaces) {
    var body = surfaces ? sectionsBody(surfaces) : {};
    body.updates = updates;

    return post(window.routes.cart_update_url, body).then(function (response) {
      if (!response.ok) throw new Error('Reconcile failed: ' + response.status);
      return response.json();
    });
  }

  // Resolves with the returned cart, or null when Shopify refused the raise
  function raiseLine(key, quantity) {
    return post(window.routes.cart_change_url, {
      id: key,
      quantity: quantity
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        return parsed && Array.isArray(parsed.items) ? parsed : null;
      })
      .catch(function (error) {
        console.warn('[addon-set] could not raise a line', error);
        return null;
      });
  }

  function renderSections(surfaces, parsedState) {
    if (!parsedState.sections) return;

    surfaces.forEach(function (surface) {
      surface.getSectionsToRender().forEach(function (section) {
        var container = document.getElementById(section.id);
        if (!container) return;

        var html = parsedState.sections[section.section];
        if (!html) return;

        var elementToReplace = container.querySelector(section.selector) || container;
        elementToReplace.innerHTML = surface.getSectionInnerHTML(html, section.selector);
      });
    });
  }

  function noteUnreachable(corrections, parsedState) {
    parsedState.items.forEach(function (item) {
      if (!Object.prototype.hasOwnProperty.call(corrections, item.key)) return;
      if (item.quantity === corrections[item.key]) return;

      unreachable[targetKey(item.key, corrections[item.key])] = true;
    });
  }

  // Skip the cart read entirely when no set row is rendered
  function mayHoldSet() {
    return !!document.querySelector('[data-lj-set]');
  }

  function reconcile() {
    var helpers = window.AddonSetRelationships;
    var surfaces = getCartSurfaces();

    if (running || !helpers || surfaces.length === 0) return;
    if (!mayHoldSet()) return;

    running = true;

    var sent = null;

    helpers
      .fetchCart()
      .then(function (cart) {
        var sets = helpers.findSets(cart);
        if (sets.length === 0) return null;

        var plan = buildTargets(sets);
        var corrections = buildCorrections(cart, plan.targets);
        if (!corrections) return null;

        sent = corrections;

        return applyCorrections(cart, corrections, plan, surfaces);
      })
      .then(function (parsedState) {
        if (!parsedState) return;

        noteUnreachable(sent, parsedState);
        renderSections(surfaces, parsedState);
        // Does not republish cartUpdate, so a repair cannot retrigger itself
      })
      .catch(function (error) {
        console.warn('[addon-set] reconcile skipped', error);
      })
      .then(function () {
        running = false;
      });
  }

  function start() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === SOURCE) return;

        // cart.js already levelled this set
        if (event && event.setLevelled) return;

        reconcile();
      });
    }

    // Wait for cart.js to define cart-items before the load-time pass
    if (document.querySelector('cart-items')) {
      customElements.whenDefined('cart-items').then(reconcile);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
